import { useToast } from '@chakra-ui/react'
import { dehydrate, QueryClient } from '@tanstack/react-query'
import { serverSideTrackEvent } from '@twlvxtwlv/analytics/browser'
import { _trackEventOnServerWithLogging } from '@twlvxtwlv/analytics/server'
import { getCampaignPageAnalyticsEventForSlug } from '@twlvxtwlv/analytics/utils'
import type { ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import { type CampaignEntity } from '@twlvxtwlv/cms'
import type { CurrentNftWithProduct } from '@twlvxtwlv/mapping'
import { toCampaignSsrProps } from '@twlvxtwlv/mapping'
import {
  type ArtistWithProducts,
  getCustomerWithoutJwtByIri,
  getIsProductAward,
  getProductsByArtist,
  type Product,
  toArtistsWithProducts,
  toCustomerIri,
} from '@twlvxtwlv/tokenforge'
import type {
  CampaignDetailsSlugType,
  CampaignNavigationRecords,
  NewsletterOptInInput,
  NotifyMeAttributes,
  NotifyMeOptInInput,
  RoadmapEntry,
} from '@twlvxtwlv/types'
import { CampaignDetails } from '@twlvxtwlv/ui/src/components/pages/marketplace/campaign-details'
import { Page } from '@twlvxtwlv/ui/src/components/shared/page'
import { extractFirstQueryParamValue } from '@twlvxtwlv/utils/routing'
import { _initDistinctId, initDistinctId } from '@twlvxtwlv/utils/src/cookie'
import { logger } from '@twlvxtwlv/utils/src/logging'
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useState } from 'react'
import { setSwrCachingHeader } from '@twlvxtwlv/utils/src/headers'
import {
  getUseAuctionsWithHighestBidOptions,
  useAuctionsWithHighestBid,
} from '@twlvxtwlv/hooks/src/supabase'
import { getUseSimilarProductsOptions, useSimilarProducts } from '@twlvxtwlv/hooks'
import { getUseCampaignOptions } from '@twlvxtwlv/hooks/src/cms/use-campaign'

interface CampaignPageProps {
  artistsWithProducts: ArtistWithProducts[]
  allProducts: Product[]
  backgroundImageUrl: string
  campaignEntity: CampaignEntity
  campaignNavigationRecords: CampaignNavigationRecords
  currentNftsWithProducts: CurrentNftWithProduct[]
  roadmapEntries: RoadmapEntry[]
  campaignId: string
  campaignContainsAward: boolean
  genre: string
  locale: string
  similarProductsPerPage: number
}

export default function CampaignPage({
  artistsWithProducts,
  allProducts,
  backgroundImageUrl,
  campaignEntity,
  campaignNavigationRecords,
  currentNftsWithProducts,
  roadmapEntries,
  campaignId,
  campaignContainsAward,
  genre,
  locale,
  similarProductsPerPage,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation(['common, campaign'])
  const toast = useToast()
  const [selectedNftWithProduct, setSelectedNftWithProduct] =
    useState<CurrentNftWithProduct | null>(null)
  const [notifyMeAttributes, setNotifyMeAttributes] = useState<NotifyMeAttributes | null>(null)

  const { auctionsWithHighestBid } = useAuctionsWithHighestBid()

  const { similarProducts } = useSimilarProducts({
    locale,
    genre: genre,
    itemsPerPage: similarProductsPerPage,
  })

  function handleOnSelectedNftWithProductChanged(newNftWithProduct: CurrentNftWithProduct | null) {
    setSelectedNftWithProduct(newNftWithProduct)
  }

  const distinctId = initDistinctId()

  async function handleOnAddNewsletterContact(
    newsletterOptInInput: NewsletterOptInInput,
    onSuccess: () => void
  ) {
    const newsletterId = campaignEntity?.attributes?.newsletterID
    const { email } = newsletterOptInInput
    if (!email) {
      console.error(`Missing email while signing up: ${email}`)
      toast({
        title: t('campaign:newsletter-missing-email-title'),
        position: 'top-right',
        status: 'error',
      })

      return
    }
    if (!newsletterId) {
      console.error(`Missing newsletter ID while signing up: ${newsletterId}`)
      toast({
        title: t('campaign:newsletter-error-title'),
        position: 'top-right',
        status: 'error',
      })
      return
    }

    const newsletterResponse = await fetch('/api/sendinblue/create-contact', {
      method: 'PUT',
      body: JSON.stringify({ email, newsletterId }),
    })
    if (newsletterResponse.status === 201) {
      serverSideTrackEvent(
        { userId: distinctId },
        {
          eventName: 'campaign-page-sign-up-newsletter-started',
          artistName: artistsWithProducts[0]?.artist?.profileName ?? 'MISSING_VALUE',
          campaignId: campaignEntity?.id ?? 'MISSING_VALUE',
          campaignSlug: campaignId ?? 'MISSING_VALUE',
          artistId: artistsWithProducts[0]?.artist?.uuidAsString ?? 'MISSING_VALUE',
          newsletterListId: newsletterId ?? 'MISSING_VALUE',
        }
      )
      onSuccess()
    }
  }

  async function handleNotifyMeSubmit(
    notifyMeOptInInput: NotifyMeOptInInput,
    onSuccess: () => void
  ) {
    const newsletterId = campaignEntity?.attributes?.newsletterID
    const { email } = notifyMeOptInInput
    if (!email) {
      console.error(`Missing email while signing up: ${email}`)
      toast({
        title: t('campaign:newsletter-missing-email-title'),
        position: 'top-right',
        status: 'error',
      })

      return
    }
    if (!newsletterId) {
      console.error(`Missing newsletter ID while signing up: ${newsletterId}`)
      toast({
        title: t('campaign:newsletter-error-title'),
        position: 'top-right',
        status: 'error',
      })
      return
    }

    const attributes = {
      PRODUCT_ID:
        notifyMeAttributes?.PRODUCT_ID ?? selectedNftWithProduct?.product?.code ?? 'MISSING_VALUE',
      PRODUCT_NAME:
        notifyMeAttributes?.PRODUCT_NAME ??
        selectedNftWithProduct?.product?.name ??
        'MISSING_VALUE',
    }

    const NotifyMeResponse = await fetch('/api/sendinblue/create-contact', {
      method: 'PUT',
      body: JSON.stringify({
        email,
        newsletterId,
        attributes,
      }),
    })
    if (NotifyMeResponse.status === 201) {
      serverSideTrackEvent(
        { userId: distinctId },
        {
          eventName: 'campaign-page-sign-up-notify-me-started',
          artistName: artistsWithProducts[0]?.artist?.profileName ?? 'MISSING_VALUE',
          campaignId: campaignEntity?.id ?? 'MISSING_VALUE',
          campaignSlug: campaignId ?? 'MISSING_VALUE',
          artistId: artistsWithProducts[0]?.artist?.uuidAsString ?? 'MISSING_VALUE',
          newsletterListId: newsletterId ?? 'MISSING_VALUE',
          ProductId: selectedNftWithProduct?.currentNft?.nftId ?? 'MISSING_VALUE',
          ProductName: selectedNftWithProduct?.currentNft?.title ?? 'MISSING_VALUE',
        }
      )
      onSuccess()
    }
  }

  const { status: authenticationStatus } = useSession()

  function handleOnTrackAllNftsClicked() {
    serverSideTrackEvent(
      { userId: distinctId },
      {
        eventName: 'campaign-page-other-nfts-from-artist-clicked',
        artistName: artistsWithProducts[0]?.artist?.profileName ?? 'MISSING_VALUE',
        campaignId: campaignEntity?.id ?? 'MISSING_VALUE',
        campaignSlug: campaignId ?? 'MISSING_VALUE',
        artistId: artistsWithProducts[0]?.artist?.uuidAsString ?? 'MISSING_VALUE',
      }
    )
  }

  function handleOnTrackNavigation(
    slug: CampaignDetailsSlugType,
    product: Product | undefined | null
  ) {
    const artistId = product?.creator?.uuidAsString
    const artistName = product?.creatorProfileName?.toString()
    const nftName = product?.name
    const nftId = product?.code
    const event = getCampaignPageAnalyticsEventForSlug(
      slug,
      campaignId,
      campaignEntity?.id?.toString(),
      artistName,
      artistId,
      nftName?.toString(),
      nftId
    )

    if (event) {
      serverSideTrackEvent({ userId: distinctId }, event)
    }
  }

  function handleOnTrackPurchaseButtonClicked(
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) {
    const product = allProducts.find((product) => product?.code === currentNft.nftId)

    serverSideTrackEvent(
      { userId: distinctId },
      {
        eventName: 'campaign-page-nft-purchase-button-clicked',
        nftPrice: priceInEuroCents / 100,
        campaignSlug: campaignEntity?.id?.toString() ?? 'MISSING_VALUE',
        campaignId: campaignId ?? 'MISSING_VALUE',
        nftName: product?.name ?? 'MISSING_VALUE',
        nftId: product?.code ?? 'MISSING_VALUE',
        artistName: product?.creatorProfileName ?? 'MISSING_VALUE',
        artistId: product?.creator?.uuidAsString ?? 'MISSING_VALUE',
      }
    )
  }

  function handleOnTrackLearnMoreButtonClicked(
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) {
    const product = allProducts.find((product) => product?.code === currentNft.nftId)

    serverSideTrackEvent(
      { userId: distinctId },
      {
        eventName: 'campaign-page-nft-learn-more-button-clicked',
        nftPrice: priceInEuroCents / 100,
        campaignId: campaignId ?? 'MISSING_VALUE',
        campaignSlug: campaignEntity?.id?.toString() ?? 'MISSING_VALUE',
        nftName: product?.name ?? 'MISSING_VALUE',
        nftId: product?.code ?? 'MISSING_VALUE',
        artistName: product?.creatorProfileName ?? 'MISSING_VALUE',
        artistId: product?.creator?.uuidAsString ?? 'MISSING_VALUE',
      }
    )
  }

  return (
    <>
      <Head>
        <title>{t('common:meta-title-campaign')}</title>
        <meta
          name="description"
          content={campaignEntity.attributes?.campaignContents?.headline ?? 'Campaign'}
        />
      </Head>
      <Page
        bgColor="none"
        bgImage={backgroundImageUrl}
        bgRepeat={true}
        authenticationStatus={authenticationStatus}
        isCampaign
      >
        <CampaignDetails
          artistsWithProducts={artistsWithProducts}
          auctionsWithHighestBid={auctionsWithHighestBid}
          campaignContainsAward={campaignContainsAward}
          campaignEntity={campaignEntity}
          campaignNavigationRecords={campaignNavigationRecords}
          currentNftsWithProducts={currentNftsWithProducts}
          genre={genre}
          onAddNewsletterContact={handleOnAddNewsletterContact}
          onNotifyMeAttributesChanged={setNotifyMeAttributes}
          onNotifyMeSubmit={handleNotifyMeSubmit}
          onSelectedNftWithProductChanged={handleOnSelectedNftWithProductChanged}
          onTrackAllNftsClicked={handleOnTrackAllNftsClicked}
          onTrackLearnMoreButtonClicked={handleOnTrackLearnMoreButtonClicked}
          onTrackNavigation={handleOnTrackNavigation}
          onTrackPurchaseButtonClicked={handleOnTrackPurchaseButtonClicked}
          roadmapEntries={roadmapEntries}
          similarProducts={similarProducts}
        />
      </Page>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<CampaignPageProps> = async ({
  locale = 'de',
  query,
  req,
  res,
}: GetServerSidePropsContext) => {
  setSwrCachingHeader(res)
  const campaignId = extractFirstQueryParamValue(query.id)
  const distinctId = _initDistinctId({ req, res })
  if (!campaignId) {
    return {
      notFound: true,
    }
  }

  const currentNftsPage = 1
  const currentNftsPageSize = 40

  const queryClient = new QueryClient()

  const campaignRequest = queryClient.fetchQuery(
    getUseCampaignOptions({
      locale,
      id: campaignId,
      currentNftsPageSize,
      currentNftsPage,
    })
  )

  const auctionsRequest = queryClient.fetchQuery(getUseAuctionsWithHighestBidOptions())

  const genre = 'ElectronicMusic' // The hardcoded genre for now

  const similarProductsPerPage = 6

  const similarProductsRequest = queryClient.prefetchQuery(
    getUseSimilarProductsOptions({
      locale,
      genre: genre,
      itemsPerPage: similarProductsPerPage,
    })
  )

  const [campaignResponse, auctionsResponse, similarProductsResponse] = await Promise.allSettled([
    campaignRequest,
    auctionsRequest,
    similarProductsRequest,
  ])

  let campaignEntity: CampaignEntity | null = null

  if (campaignResponse?.status === 'fulfilled') {
    const campaignEntities = campaignResponse.value.campaigns?.data
    campaignEntity =
      campaignEntities && campaignEntities.length > 0
        ? (campaignEntities[0] as CampaignEntity)
        : null
  } else {
    logger.error(`Error while fetching campaign with campaignId ${campaignId}`)
    return {
      notFound: true,
    }
  }

  if (!campaignEntity || !campaignEntity.attributes) {
    return {
      notFound: true,
    }
  }

  if (auctionsResponse.status === 'rejected') {
    logger.error('Error while fetching auctions with highest bids from DB')
  }

  if (similarProductsResponse?.status === 'rejected') {
    logger.error(`Error while fetching products with genre ${genre}`)
  }

  const artistUuids = (campaignEntity.attributes?.tokenForge?.Artists ?? [])
    .map((artist) => artist?.TokenForge_UUID)
    .filter((artistUuid) => artistUuid) as string[]

  const requestsPromises = artistUuids.flatMap((artistUuid) => {
    return [
      queryClient.fetchQuery({
        queryKey: ['customerWithoutJwt', artistUuid],
        queryFn: () =>
          getCustomerWithoutJwtByIri({
            iri: toCustomerIri(artistUuid),
          }),
      }),
      queryClient.fetchQuery({
        queryKey: ['productsByArtist', artistUuid],
        queryFn: () =>
          getProductsByArtist({
            locale,
            creatorUuid: artistUuid,
          }),
      }),
    ]
  })

  const responses = await Promise.allSettled(requestsPromises)

  const artistsWithProducts: ArtistWithProducts[] = toArtistsWithProducts(responses, campaignId)

  const {
    allProducts,
    backgroundImageUrl,
    campaignNavigationRecords,
    currentNftsWithProducts,
    roadmapEntries,
  } = await toCampaignSsrProps(artistsWithProducts, campaignEntity, locale)

  const campaignContainsAward = currentNftsWithProducts.some((currentNft) =>
    getIsProductAward(currentNft.product)
  )

  const userAgent = req.headers['user-agent'] as string

  _trackEventOnServerWithLogging(
    { userId: distinctId },
    {
      eventName: 'campaign-page-visited',
      campaignId: campaignEntity?.id ?? 'MISSING_VALUE',
      artistName: artistsWithProducts[0]?.artist?.profileName ?? 'MISSING_VALUE',
      campaignSlug: campaignId ?? 'MISSING_VALUE',
    },
    userAgent
  )

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      artistsWithProducts,
      allProducts,
      backgroundImageUrl,
      campaignEntity,
      campaignNavigationRecords,
      currentNftsWithProducts,
      roadmapEntries,
      campaignId,
      locale,
      campaignContainsAward,
      genre,
      similarProductsPerPage,
      ...(await serverSideTranslations(locale, [
        'common',
        'campaign',
        'nft-details',
        'auction',
        'checkout',
        'navigation',
        'footer',
        'user-management',
      ])),
    },
  }
}
